import { useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';

// material-ui
import {
    Avatar,
    Box,
    ClickAwayListener,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    Paper,
    Popper,
    Stack,
    Typography,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// third-party
import { FormattedMessage, useIntl } from 'react-intl';
import { format } from 'date-fns';

// project imports
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { ApiLicensesPostCollection } from 'store/app/admin/license';
import Transitions from 'ui-component/extended/Transitions';
import MainCard from 'ui-component/cards/MainCard';
import FileUploader from 'ui-component/fields/FileUploader';

// project hooks
import useAuth from 'hooks/useAuth';

// assets
import CreditScoreTwoToneIcon from '@mui/icons-material/CreditScoreTwoTone';
import CreditCardOffTwoToneIcon from '@mui/icons-material/CreditCardOffTwoTone';

// ==============================|| LICENSE ||============================== //

const LicenseSection = () => {
    const dispatch = useDispatch();
    const { user, license, updateLicense } = useAuth();

    const theme = useTheme();
    const intl = useIntl();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

    const [newLicense, setNewLicense] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const prevOpen = useRef(open);

    const getEntityLimitColor = (count, limit) => {
        const numericCount = Number(count);
        const numericLimit = Number(limit);

        if (Number.isFinite(numericCount) && Number.isFinite(numericLimit)) {
            if (numericCount >= numericLimit) {
                return 'error';
            }

            if (numericCount > numericLimit * 0.9) {
                return 'warning';
            }
        }

        return 'success';
    };

    const getEntityLimitSx = (count, limit) => {
        const color = getEntityLimitColor(count, limit);

        return {
            backgroundColor: theme.palette[color].light,
            color: theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette[color].dark
        };
    };

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Box
                sx={{
                    ml: 2,
                    [theme.breakpoints.down('md')]: { ml: 1 }
                }}
            >
                <Avatar
                    variant="rounded"
                    sx={{
                        ...theme.typography.commonAvatar,
                        ...theme.typography.mediumAvatar,
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        color: theme.palette.primary.dark,
                        transition: 'all .2s ease-in-out',
                        '&[aria-controls="menu-list-grow"],&:hover': {
                            borderColor: theme.palette.primary.main,
                            background: theme.palette.primary.main,
                            color: theme.palette.primary.light
                        }
                    }}
                    ref={anchorRef}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    onClick={() => setOpen((prevOpen) => !prevOpen)}
                    color="inherit"
                >
                    {license?.state === 'missing' && <CreditCardOffTwoToneIcon color="disabled" fontSize="small" />}
                    {license?.state === 'invalid' && <CreditCardOffTwoToneIcon color="error" fontSize="small" />}
                    {license?.state === 'expired' && <CreditCardOffTwoToneIcon color="warning" fontSize="small" />}
                    {license?.state === 'active' && <CreditScoreTwoToneIcon color="success" fontSize="small" />}
                </Avatar>
            </Box>
            <Popper
                placement={matchesXs ? 'bottom-start' : 'bottom'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 14] } }] }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener
                        onClickAway={(event) => {
                            if (anchorRef.current && anchorRef.current.contains(event.target)) {
                                return;
                            }

                            setOpen(false);
                        }}
                    >
                        <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                            <Paper sx={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Box sx={{ p: 2, pb: 0 }}>
                                            <Typography variant="h4">
                                                <FormattedMessage id="ui-comp-license-info" />
                                            </Typography>
                                            <Divider />
                                        </Box>
                                        <Box sx={{ p: 2, pb: 0 }}>
                                            <Grid
                                                container
                                                direction="column"
                                                justifyContent="center"
                                                alignItems="center"
                                                sx={{ p: 2, pt: 0 }}
                                            >
                                                {license?.state && (
                                                    <Grid item container direction="row" justifyContent="space-between" alignItems="center">
                                                        <Grid item xs={4}>
                                                            <Typography variant="subtitle1">
                                                                <FormattedMessage id="ui-comp-license-state" />:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs>
                                                            <Typography variant="body2">
                                                                <FormattedMessage id={`ui-comp-license-state-${license.state}`} />
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                                {license?.type && (
                                                    <Grid item container direction="row" justifyContent="space-between" alignItems="center">
                                                        <Grid item xs={4}>
                                                            <Typography variant="subtitle1">
                                                                <FormattedMessage id="ui-comp-license-type" />:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs>
                                                            <Typography variant="body2">
                                                                <FormattedMessage id={`ui-comp-license-type-${license.type}`} />
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                                {license?.client && (
                                                    <Grid item container direction="row" justifyContent="space-between" alignItems="center">
                                                        <Grid item xs={4}>
                                                            <Typography variant="subtitle1">
                                                                <FormattedMessage id="ui-comp-license-client" />:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs>
                                                            <Typography variant="body2">{license.client}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                                {license?.maxSimConnsAllowed && (
                                                    <Grid item container direction="row" justifyContent="space-between" alignItems="center">
                                                        <Grid item xs={4}>
                                                            <Typography variant="subtitle1">
                                                                <FormattedMessage id="ui-comp-license-maxsimconnsallowed" />:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs>
                                                            <Typography variant="body2">{license.maxSimConnsAllowed}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                                {license?.entityPersLimits && (
                                                    <Grid item container direction="row" justifyContent="space-between" alignItems="center">
                                                        <Grid item xs={12}>
                                                            <Typography variant="subtitle1">
                                                                <FormattedMessage id="ui-comp-license-entityperslimits" />:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <List dense>
                                                                {license?.entityPersLimits?.map((entityPersLimit, rowIndex) => (
                                                                    <ListItem key={`entityperslimit${rowIndex}`}>
                                                                        <ListItemText
                                                                            primary={
                                                                                <Stack
                                                                                    direction="row"
                                                                                    justifyContent="space-between"
                                                                                    alignItems="center"
                                                                                    sx={{
                                                                                        px: 1,
                                                                                        py: 0.5,
                                                                                        borderRadius: 1,
                                                                                        ...getEntityLimitSx(
                                                                                            entityPersLimit?.count,
                                                                                            entityPersLimit?.limit
                                                                                        )
                                                                                    }}
                                                                                >
                                                                                    <Typography variant="body2">
                                                                                        <FormattedMessage id={entityPersLimit?.label} />
                                                                                    </Typography>
                                                                                    <Typography variant="body2">
                                                                                        {entityPersLimit?.count}/{entityPersLimit?.limit}
                                                                                    </Typography>
                                                                                </Stack>
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                                {license?.expiresAt && (
                                                    <Grid item container direction="row" justifyContent="space-between" alignItems="center">
                                                        <Grid item xs={4}>
                                                            <Typography variant="subtitle1">
                                                                <FormattedMessage id="ui-comp-license-expiresat" />:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs>
                                                            <Typography variant="body2">
                                                                {format(new Date(license.expiresAt), 'd/MM/yyyy HH:mm')}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Box>
                                        {user?.authorizations?.includes('system-license-canAdd') && (
                                            <Box sx={{ p: 2, pt: 0 }}>
                                                <FileUploader
                                                    multiple={false}
                                                    handleAddFiles={(newFiles) => {
                                                        setNewLicense(newFiles[newFiles?.length - 1]?.fileOriginalName);
                                                        setIsUploading(true);

                                                        const fileFormData = new FormData();
                                                        fileFormData.append('license', newFiles[newFiles?.length - 1]?.file);

                                                        ApiLicensesPostCollection({
                                                            data: fileFormData,
                                                            successCallback: (response) =>
                                                                batch(() => {
                                                                    setIsUploading(false);
                                                                    updateLicense(response);
                                                                    window.location.reload(true);
                                                                }),
                                                            errorCallback: (error) =>
                                                                batch(() => {
                                                                    setIsUploading(false);
                                                                    dispatch(
                                                                        openSnackbar({
                                                                            open: true,
                                                                            message:
                                                                                error ||
                                                                                intl.formatMessage({
                                                                                    id: 'ui-comp-snackbar-genericerror-text'
                                                                                }),
                                                                            variant: 'alert',
                                                                            alert: { color: 'error' }
                                                                        })
                                                                    );
                                                                })
                                                        });
                                                    }}
                                                    accept="application/JSON"
                                                    formIsSubmitting={isUploading}
                                                    text={
                                                        isUploading && newLicense ? (
                                                            <Grid
                                                                container
                                                                direction="column"
                                                                justifyContent="flex-end"
                                                                alignItems="center"
                                                            >
                                                                <Grid item>
                                                                    <Typography variant="caption" sx={{ textTransform: 'none' }}>
                                                                        <FormattedMessage id="ui-comp-license-uploading" />
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Typography variant="caption" sx={{ textTransform: 'none' }}>
                                                                        {newLicense}
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        ) : (
                                                            <Typography variant="caption" sx={{ textTransform: 'none' }}>
                                                                <FormattedMessage id="ui-comp-license-uploadnew" />
                                                            </Typography>
                                                        )
                                                    }
                                                    size="small"
                                                    direction="row"
                                                />
                                            </Box>
                                        )}
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default LicenseSection;
